import _ from 'lodash';
import React, {FC, useCallback, useContext, useLayoutEffect, useMemo, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/helpers/src/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {KeyboardEventHandlers} from '../../../../libs/shared/ui/src/components/keyboard/keyboardContext';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {boxShadow} from '../../../../libs/shared/ui/src/theme/themeStyleHelpers';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {FrameViewerState} from '../../../../srcCommon/inboxes/side/sideFrameActions';
import {usePush} from '../../../../srcCommon/push/pushContext';
import {QueryClientProvider} from '../../../../srcCommon/services/queryClient';
import {useMeasureElement} from '../../../helpers/hooks/domHookHelpers';
import {useAppDispatch} from '../../../helpers/reduxHelpers';
import {computeSidePanelTrackingTrackingFramePayload} from '../../../helpers/sidePanelTrackingHelpers';
import {FrameTypesEnum} from '../../../models/api/apiTypes';
import {ApplicationEntryPointActionSdkVersionsEnum} from '../../../models/api/applicationEntryPointActionModel';
import {ApplicationEntryPointModel} from '../../../models/api/applicationEntryPointModel';
import {ConversationBaseModel, ConversationModel} from '../../../models/api/conversationModel';
import {IntegrationModel} from '../../../models/api/integrationModel';
import {SidepanelMultiViewDetailsModel} from '../../../models/api/localPreferencesModel';
import {PluginModel} from '../../../models/api/pluginModel';
import {OnboardingTipTypesEnum} from '../../../models/api/preferencesModel';
import {popoutApplicationAction} from '../../../store/panels/applications/applicationPanelActions';
import {ApplicationPanelLocationsEnum} from '../../../store/panels/applications/applicationPanelState';
import {CellsContext} from '../../../store/panels/cells/context/cellsContext';
import {ContactDetailsPanelLocationsEnum} from '../../../store/panels/contacts/contactDetailsPanelState';
import {
  makeCalendarsPanelId,
  makeContactDetailsPanelId,
  ViewerPanelId,
} from '../../../store/panels/panelStateBase';
import {keyboardHelpFrame, SidePanelFrame} from '../../../store/stateless/sidePanelFrameSelectors';
import {topBarHeight} from '../../app/topBar/topBarHelpers';
import {AppEntrypointLegacySdkContainer} from '../../applications/appEntryPointLegacySdkContainer';
import {ConversationsApplicationContextHostContainer} from '../../applications/contextHosts/conversationsApplicationContextHostContainer';
import {LegacyConversationsApplicationContextHostContainer} from '../../applications/legacyContextHosts/legacyConversationsApplicationContextHostContainer';
import {SideCalendarsContainer} from '../../calendars/sideCalendarsContainer';
import {useInfoBubblesContext} from '../../common/infoBubbles/infoBubblesContext';
import {ResizeSeparator, ResizeSeparatorSidesEnum} from '../../common/resizeSeparator';
import {TransientContactDetailsContainer} from '../../contacts/contactDetailsContainer';
import {ContactDetailsTrackingHandler} from '../../contacts/contactDetailsTrackingHelpers';
import {GrammarlyContext} from '../../grammarly/grammarlyContext';
import {KeyboardHelp} from '../../keyboardHelp/keyboardHelp';
import {StartGuideContainer} from '../../startGuide/startGuideContainer';
import {SidePanelApplicationsManagerContainer} from './applicationsManager/sidePanelApplicationsManagerContainer';
import {useNewIntegrationsTrackingHook} from './hooks/useNewIntegrationsTrackingHook';
import {IntegrationFrameContainer} from './integrationFrameContainer';
import {PluginFrameContainer} from './pluginFrameContainer';
import {useWasVisibleOnce} from './sideFrameHooks';
import {SidePanelContext} from './sidePanelContext';
import {SidePanelHeader} from './sidePanelHeader';
import {useSelectedFrameLogging} from './sidePanelLogging';
import {SidePanelOfflinePlaceholder} from './sidePanelOfflinePlaceholder';
import {MultiViewContent} from './switcher/multiView/multiViewContent';
import {SidePanelSwitcherContainer} from './switcher/sidePanelSwitcherContainer';

/*
 * Constants.
 */

export const minSidePanelWidth = 322;
export const maxSidePanelWidth = 600;
export const sidePanelSwitcherWidth = 64;

/*
 * Props.
 */

export interface SidePanelPublicProps {
  isResizing: boolean;
  overlayWidthTreshold: number;
  openWidth: number;
  onIsResizingChange?: HandlerOf<boolean>;
  onRequestWidthChange: HandlerOf<number>;
}

interface SidePanelProps extends SidePanelPublicProps {
  frames: ReadonlyArray<SidePanelFrame>;
  visibleFrames: ReadonlyArray<SidePanelFrame>;
  hasIntegrationFrames: boolean;
  lastSelectedFrameId: string | undefined;
  selectedFrameId: string | undefined;
  viewerState?: FrameViewerState;
  onlyConversation: ConversationModel | undefined;
  selectedConversations: ReadonlyArray<ConversationBaseModel>;
  panelId: ViewerPanelId;
  cellsContext: CellsContext;
  poppedOutFrames: ReadonlyArray<string>;
  onFrameIdSelect: HandlerOf<string | undefined>;
  multiViewDetails: SidepanelMultiViewDetailsModel;
  hasP4PopOutAnyPlugin?: boolean;
}

/*
 * Style.
 */

interface SideStyleProps {
  $isAbove: boolean;
}
const StyledSideDiv = styled.div<SideStyleProps>`
  display: grid;
  grid-auto-flow: column;
  grid-template-columns: auto 1fr;
  grid-template-rows: 100%;

  ${(p) => addSideLayoutStyle(p.$isAbove)};
`;

function addSideLayoutStyle(isAbove: boolean) {
  if (isAbove) {
    return css`
      position: absolute;
      top: 0;
      right: 0;
      bottom: 0;
      ${(p) => boxShadow(p.theme, `0 0 10px ${p.theme.alphas.gray10}`)};

      > ${StyledPanelDiv} {
        // Needed to make the height work on Safari when side panel is above P3.
        height: calc(100vh - ${topBarHeight}px);
      }
    `;
  }

  return css`
    grid-row: 1;
    grid-column: 6;
  `;
}

const StyledPanelDiv = styled.div`
  background-color: ${(p) => p.theme.foreground};

  display: grid;
  grid-template-columns: 1fr auto;
  grid-auto-flow: column;
`;

const StyledContentWrapperDiv = styled.div`
  min-height: 0;
  position: relative;
  background-color: ${(p) => p.theme.foreground};
`;

const StyledContentDiv = styled.div`
  min-height: 0;
  height: 100%;

  display: grid;
  grid-template-rows: auto 1fr;
  grid-auto-flow: row;
`;

interface FramesStyleProps {
  $isResizing: boolean;
}
const StyledFramesDiv = styled.div<FramesStyleProps>`
  position: relative;
  pointer-events: ${(p) => (p.$isResizing ? 'none' : 'inherit')};
`;

interface CalendarFrameStyleProps {
  $isSelected: boolean;
}
const StyledCalendarFrameDiv = styled.div<CalendarFrameStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  overflow: hidden;
  visibility: ${(p) => (p.$isSelected ? 'visible' : 'hidden')};
`;

const StyledContactDetailsEmptyStateWrapperDiv = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: grid;
  align-content: center;
`;

const StyledContactDetailsEmptyStateMessageDiv = styled.div`
  text-align: center;
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.semibold};
`;

const StyledMultiViewContentWrapperDiv = styled.div`
  min-height: 0;
  position: relative;
  background-color: ${(p) => p.theme.foreground};
  display: flex;
  flex-flow: column;
  padding: 8px;
`;

const StyledPoppedOutWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  gap: 24px;
  background: ${(p) => p.theme.greys.white};
  align-items: center;
  justify-content: center;
`;

const StyledPoppedOutIconWrapperDiv = styled.div`
  padding: 32px;
  border-radius: 9000px;
  background: ${(p) => p.theme.greys.shade20};
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledIcon = styled(Icon)`
  color: ${(p) => p.theme.greys.shade60};
`;

/*
 * Component.
 */

export const SidePanel: FC<SidePanelProps> = (props) => {
  const {frames, lastSelectedFrameId, selectedFrameId, overlayWidthTreshold, openWidth, panelId} = props;
  const [sidepanelRef, {width: sidepanelWidth}] = useMeasureElement();

  useNewIntegrationsTrackingHook(panelId);

  /*
   * State.
   */

  const lastSelectedFrame = useMemo(
    () => frames.find((f) => f.frameId === lastSelectedFrameId),
    [frames, lastSelectedFrameId],
  );

  const selectedFrame = useMemo(
    () => frames.find((f) => f.frameId === selectedFrameId),
    [frames, selectedFrameId],
  );

  const {setRightSidebarWidth} = useContext(GrammarlyContext);
  useLayoutEffect(() => {
    setRightSidebarWidth(sidepanelWidth);
  }, [sidepanelWidth, setRightSidebarWidth]);

  /*
   * Render.
   */

  const isSidePanelOpen = Boolean(selectedFrame || selectedFrameId === FrameTypesEnum.MULTI_VIEW);
  const isAbove = isSidePanelOpen
    ? overlayWidthTreshold < openWidth
    : overlayWidthTreshold < sidePanelSwitcherWidth;

  return (
    <StyledSideDiv
      ref={sidepanelRef}
      $isAbove={isAbove}
      style={{width: isSidePanelOpen ? openWidth : undefined}}
    >
      <SidePanelContent {...props} lastSelectedFrame={lastSelectedFrame} selectedFrame={selectedFrame} />
      {isSidePanelOpen && (
        <ResizeSeparator
          column={1}
          size={openWidth}
          minSize={minSidePanelWidth}
          maxSize={maxSidePanelWidth}
          onSizeChange={props.onRequestWidthChange}
          onIsResizingChange={props.onIsResizingChange}
          side={ResizeSeparatorSidesEnum.LEFT}
        />
      )}
    </StyledSideDiv>
  );
};

interface SidePanelContentProps extends SidePanelProps {
  lastSelectedFrame?: SidePanelFrame;
  selectedFrame?: SidePanelFrame;
}

const SidePanelContent: FC<SidePanelContentProps> = (props) => {
  const {
    frames,
    visibleFrames,
    lastSelectedFrame,
    selectedFrame,
    multiViewDetails,
    onFrameIdSelect,
    viewerState,
    selectedFrameId: sidepanelSelectedFrameId,
  } = props;
  const selectedFrameId = selectedFrame && selectedFrame.frameId;

  const {selectedFrameIds, frameHeights} = multiViewDetails;
  const isMultiViewSelected = sidepanelSelectedFrameId === FrameTypesEnum.MULTI_VIEW;

  const dispatch = useAppDispatch();
  const {isConnected} = usePush();
  const lastOnlineFrameIdRef = useRef<string>();
  const [isDragging, setIsDragging] = useState(false);

  // Remember the last frame we successfully displayed online.
  if (isConnected) {
    lastOnlineFrameIdRef.current = selectedFrameId;
  }

  // Show the offline UI when we're disconnected and switch away from the last successful frame.
  const isShowingOfflineUI = !isConnected && selectedFrameId !== lastOnlineFrameIdRef.current;

  useSelectedFrameLogging(selectedFrame, lastSelectedFrame);

  const plugins = useMemo(
    () =>
      _(frames)
        .map((f) => f.frameType === FrameTypesEnum.PLUGIN && f)
        .compact()
        .value(),
    [frames],
  );
  const integrations = useMemo(
    () =>
      _(frames)
        .map((f) => f.frameType === FrameTypesEnum.INTEGRATION && f)
        .compact()
        .value(),
    [frames],
  );
  const applicationEntryPointsLegacySdk = useMemo(
    () =>
      _(frames)
        .map(
          (f) =>
            f.frameType === FrameTypesEnum.APPLICATION_ENTRY_POINT &&
            f.action?.sdkVersion === ApplicationEntryPointActionSdkVersionsEnum.LEGACY &&
            f,
        )
        .compact()
        .value(),
    [frames],
  );

  const {closeInfoBubbles} = useInfoBubblesContext();

  const openFrame = useCallback(
    (frameId: string) => {
      if (frameId !== FrameTypesEnum.START_GUIDE) {
        closeInfoBubbles(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER);
      }

      onFrameIdSelect(frameId);
    },
    [closeInfoBubbles, onFrameIdSelect],
  );

  const closeFrame = useCallback(() => {
    closeInfoBubbles(OnboardingTipTypesEnum.START_GUIDE_COMMENT_COMPOSER);
    onFrameIdSelect(undefined);
    lastOnlineFrameIdRef.current = undefined;
  }, [closeInfoBubbles, onFrameIdSelect, lastOnlineFrameIdRef]);

  const onFrameSelect = useCallback(
    (frame: SidePanelFrame) => {
      if (selectedFrame === frame) {
        closeFrame();
      } else {
        openFrame(frame.frameId);
      }
    },
    [closeFrame, openFrame, selectedFrame],
  );

  const onOpenInPopup = (frame: SidePanelFrame) => {
    dispatch(
      popoutApplicationAction(frame, {
        width: props.openWidth,
        location: ApplicationPanelLocationsEnum.POPOUT_WINDOW,
      }),
    );
  };

  /*
   * Keyboard shortcuts.
   */

  const onToggleShortcut = useCallback(() => {
    // If a frame is selected, unselect it.
    if (selectedFrame) {
      onFrameSelect(selectedFrame);
      return;
    }

    // Otherwise, restore the last frame.
    const newFrame = lastSelectedFrame || frames[0];
    if (!newFrame) {
      return;
    }

    onFrameSelect(newFrame);
  }, [frames, lastSelectedFrame, onFrameSelect, selectedFrame]);

  const onHelpShortcut = useCallback(() => {
    onFrameSelect(keyboardHelpFrame);
  }, [onFrameSelect]);

  const onFrameOffsetShortcut = useCallback(
    (offset: number) => {
      if (!selectedFrame) {
        return;
      }

      // Find the frame to select.
      const frameIndex = visibleFrames.indexOf(selectedFrame);
      const nextFrame = visibleFrames[frameIndex + offset];
      if (frameIndex < 0 || !nextFrame) {
        return;
      }

      // If one was found, select it.
      onFrameSelect(nextFrame);
    },
    [selectedFrame, visibleFrames, onFrameSelect],
  );

  const onPreviousFrameShortcut = useCallback(() => onFrameOffsetShortcut(-1), [onFrameOffsetShortcut]);
  const onNextFrameShortcut = useCallback(() => onFrameOffsetShortcut(1), [onFrameOffsetShortcut]);

  const keyHandlers: KeyboardEventHandlers = {
    [KeyboardActionsEnum.TOGGLE_SIDE_PANEL]: onToggleShortcut,
    [KeyboardActionsEnum.TOGGLE_HELP]: onHelpShortcut,
    [KeyboardActionsEnum.FRAME_PREVIOUS]: onPreviousFrameShortcut,
    [KeyboardActionsEnum.FRAME_NEXT]: onNextFrameShortcut,
  };

  /*
   * Calendar helpers.
   */

  const calendarIsVisible = Boolean(selectedFrame && selectedFrame.frameType === FrameTypesEnum.CALENDAR);
  const calendarIsVisibleInMultiView = Boolean(
    isMultiViewSelected && selectedFrameIds.includes(FrameTypesEnum.CALENDAR),
  );
  const calendarWasVisibleOnce = useWasVisibleOnce(calendarIsVisible || calendarIsVisibleInMultiView);

  // The calendar needs to be memoized to keep the reference around otherwise the calendar will throw an error
  // since it expects the panel to be always there after it is rendered.
  const renderedCalendar = useMemo(() => {
    if (!calendarWasVisibleOnce) {
      return null;
    }

    const isCalendarFrameVisible = (calendarIsVisible || calendarIsVisibleInMultiView) && !isShowingOfflineUI;
    return (
      <StyledCalendarFrameDiv $isSelected={Boolean(isCalendarFrameVisible)}>
        <SideCalendarsContainer
          panelId={makeCalendarsPanelId(props.panelId)}
          isVisible={Boolean(isCalendarFrameVisible)}
        />
      </StyledCalendarFrameDiv>
    );
  }, [
    calendarIsVisible,
    calendarIsVisibleInMultiView,
    calendarWasVisibleOnce,
    isShowingOfflineUI,
    props.panelId,
  ]);

  /*
   * Render Helpers.
   */

  const renderContent = (frame?: SidePanelFrame, isInMultiView?: boolean) => (
    <StyledContentWrapperDiv>
      <StyledContentDiv>
        {renderStaticFrame(props, frame, viewerState, isShowingOfflineUI, onOpenInPopup)}
        <StyledFramesDiv $isResizing={props.isResizing || isDragging}>
          {renderPlugins(props, plugins, frame, isShowingOfflineUI)}
          {renderPluginIntegrations(props, integrations, frame, isShowingOfflineUI)}
          {renderIntegrations(props, frame, isShowingOfflineUI)}
          {renderLegacyEntryPoint(props, frame)}
          {renderApplicationEntryPointPluginSdk(props, frame)}
          {renderApplicationEntryPointsLegacySdk(
            props,
            frame,
            applicationEntryPointsLegacySdk,
            isShowingOfflineUI,
          )}
        </StyledFramesDiv>
        {renderOfflineState(props, frame, isShowingOfflineUI)}
      </StyledContentDiv>
      {(frame?.frameId === FrameTypesEnum.CALENDAR || !isInMultiView) && renderedCalendar}
    </StyledContentWrapperDiv>
  );

  const maybeRenderMultiView = () => {
    if (!isMultiViewSelected) {
      return null;
    }
    return (
      <StyledMultiViewContentWrapperDiv>
        {selectedFrameIds.map((sFrameId, index) => {
          const multiViewFrame = frames.find((f) => f.frameId === sFrameId);
          const shouldRenderPlaceholder = !sFrameId || !multiViewFrame;
          const frameKey = `${sFrameId ?? 'placeholder'}-${index}`;

          return (
            <MultiViewContent
              key={frameKey}
              index={index}
              shouldRenderPlaceholder={shouldRenderPlaceholder}
              frameHeights={frameHeights}
            >
              {multiViewFrame && renderContent(multiViewFrame, true)}
            </MultiViewContent>
          );
        })}
      </StyledMultiViewContentWrapperDiv>
    );
  };

  /*
   * Render.
   */

  return (
    <SidePanelContext.Provider value={{closeSidePanel: closeFrame}}>
      <KeyboardShortcuts handlers={keyHandlers}>
        <StyledPanelDiv>
          {!isMultiViewSelected && renderContent(selectedFrame)}
          {maybeRenderMultiView()}
          <SidePanelSwitcherContainer
            selectedFrame={selectedFrame}
            onFrameSelect={onFrameSelect}
            onIsDraggingChange={setIsDragging}
          />
        </StyledPanelDiv>
      </KeyboardShortcuts>
    </SidePanelContext.Provider>
  );
};

/*
 * Plugins & Integrations.
 */

function renderOfflineState(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame | undefined,
  isShowingOfflineUI: boolean,
) {
  if (!selectedFrame) {
    return null;
  }

  if (!isShowingOfflineUI) {
    return null;
  }

  return <SidePanelOfflinePlaceholder />;
}

function renderStaticFrame(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame | undefined,
  viewerState: FrameViewerState | undefined,
  isShowingOfflineUI: boolean,
  onOpenInPopup: HandlerOf<SidePanelFrame>,
) {
  if (!selectedFrame) {
    return null;
  }

  if (isShowingOfflineUI) {
    return null;
  }

  switch (selectedFrame.frameType) {
    case FrameTypesEnum.MANAGER: {
      return <SidePanelApplicationsManagerContainer />;
    }
    case FrameTypesEnum.START_GUIDE:
      return (
        <QueryClientProvider>
          <StartGuideContainer panelId={props.panelId} />
        </QueryClientProvider>
      );
    case FrameTypesEnum.KEYBOARD_HELP:
      return <KeyboardHelp />;
    case FrameTypesEnum.CUSTOMER_VIEW:
      return renderContactDetailsPanel(props, viewerState);
    case FrameTypesEnum.CALENDAR:
      return null;
    default:
      return (
        <SidePanelHeader
          title={selectedFrame.name}
          additionalActionsAfter={maybeRenderPopOutButton(props, selectedFrame, onOpenInPopup)}
        />
      );
  }
}

function maybeRenderPopOutButton(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame,
  onOpenInPopup: HandlerOf<SidePanelFrame>,
) {
  if (!props.hasP4PopOutAnyPlugin) {
    return undefined;
  }
  return (
    <UserThemeConsumer>
      {(theme) => (
        <IconButton
          iconName="popout"
          label={null}
          isDisabled={props.poppedOutFrames.includes(selectedFrame.frameId)}
          onClick={() => onOpenInPopup(selectedFrame)}
          color={theme.greys.shade80}
          hoverColor={theme.greys.shade80}
        />
      )}
    </UserThemeConsumer>
  );
}

function renderPlugins(
  props: SidePanelProps,
  plugins: ReadonlyArray<PluginModel>,
  selectedFrame: SidePanelFrame | undefined,
  isShowingOfflineUI: boolean,
) {
  return plugins.map((plugin) => {
    const isVisible = selectedFrame === plugin && !isShowingOfflineUI;
    if (isVisible && props.poppedOutFrames.includes(plugin.frameId)) {
      return renderPoppedOutNotifier();
    }

    return (
      <PluginFrameContainer
        key={plugin.id}
        panelId={props.panelId}
        plugin={plugin}
        isVisible={selectedFrame === plugin && !isShowingOfflineUI}
        viewerState={props.viewerState}
        onRequestWidthChange={props.onRequestWidthChange}
        trackingPayload={computeSidePanelTrackingTrackingFramePayload(selectedFrame)}
      />
    );
  });
}

function renderPluginIntegrations(
  props: SidePanelProps,
  integrations: ReadonlyArray<IntegrationModel>,
  selectedFrame: SidePanelFrame | undefined,
  isShowingOfflineUI: boolean,
) {
  return _(integrations)
    .map((integration) => {
      const {plugin} = integration;
      if (!plugin) {
        return null;
      }

      const isVisible = selectedFrame === integration && !isShowingOfflineUI;
      if (isVisible && props.poppedOutFrames.includes(integration.frameId)) {
        return renderPoppedOutNotifier();
      }

      return (
        <PluginFrameContainer
          key={integration.id}
          panelId={props.panelId}
          plugin={plugin}
          isVisible={isVisible}
          viewerState={props.viewerState}
          onRequestWidthChange={props.onRequestWidthChange}
          trackingPayload={computeSidePanelTrackingTrackingFramePayload(selectedFrame)}
        />
      );
    })
    .compact()
    .value();
}

function renderIntegrations(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame | undefined,
  isShowingOfflineUI: boolean,
) {
  // Only create the iframe if we have at least one integration.
  if (!props.hasIntegrationFrames) {
    return null;
  }

  const selectedIntegration = (isIntegrationFrameModel(selectedFrame) && selectedFrame) || undefined;
  const isVisible = Boolean(selectedIntegration) && !isShowingOfflineUI;

  if (isVisible && selectedIntegration && props.poppedOutFrames.includes(selectedIntegration.frameId)) {
    return renderPoppedOutNotifier();
  }

  return (
    <IntegrationFrameContainer
      panelId={props.panelId}
      isVisible={isVisible}
      integration={selectedIntegration}
      viewerState={props.viewerState}
    />
  );
}

function renderLegacyEntryPoint(props: SidePanelProps, selectedFrame: SidePanelFrame | undefined) {
  if (!selectedFrame || selectedFrame.frameType !== FrameTypesEnum.ENTRY_POINT) {
    return null;
  }

  if (props.poppedOutFrames.includes(selectedFrame.frameId)) {
    return renderPoppedOutNotifier();
  }

  const {onlyConversation, selectedConversations, panelId, cellsContext} = props;
  return (
    <LegacyConversationsApplicationContextHostContainer
      entryPoint={selectedFrame}
      onlyConversation={onlyConversation}
      conversations={selectedConversations}
      panelId={panelId}
      cellsContext={cellsContext}
    />
  );
}

function renderApplicationEntryPointPluginSdk(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame | undefined,
) {
  if (
    !selectedFrame ||
    selectedFrame.frameType !== FrameTypesEnum.APPLICATION_ENTRY_POINT ||
    !selectedFrame.action ||
    selectedFrame.action.sdkVersion === ApplicationEntryPointActionSdkVersionsEnum.LEGACY
  ) {
    return null;
  }

  if (props.poppedOutFrames.includes(selectedFrame.frameId)) {
    return renderPoppedOutNotifier();
  }

  const {onlyConversation, selectedConversations, panelId, cellsContext} = props;
  return (
    <ConversationsApplicationContextHostContainer
      entryPoint={selectedFrame}
      onlyConversation={onlyConversation}
      conversations={selectedConversations}
      panelId={panelId}
      cellsContext={cellsContext}
    />
  );
}

function renderApplicationEntryPointsLegacySdk(
  props: SidePanelProps,
  selectedFrame: SidePanelFrame | undefined,
  entryPoints: ReadonlyArray<ApplicationEntryPointModel>,
  isShowingOfflineUI?: boolean,
) {
  return entryPoints.map((entryPoint) => {
    const isVisible = selectedFrame === entryPoint && !isShowingOfflineUI;

    if (isVisible && props.poppedOutFrames.includes(entryPoint.frameId)) {
      return renderPoppedOutNotifier();
    }
    return (
      <AppEntrypointLegacySdkContainer
        key={entryPoint.applicationId}
        entryPoint={entryPoint}
        panelId={props.panelId}
        viewerState={props.viewerState}
        isVisible={isVisible}
        onRequestWidthChange={props.onRequestWidthChange}
        trackingPayload={computeSidePanelTrackingTrackingFramePayload(selectedFrame)}
      />
    );
  });
}

function renderContactDetailsPanel(props: SidePanelProps, viewerState: FrameViewerState | undefined) {
  if (!viewerState) {
    return renderContactDetailsEmptyState();
  }

  const {conversation} = viewerState;
  if (!conversation) {
    return renderContactDetailsEmptyState();
  }

  return (
    <TransientContactDetailsContainer
      viewerPanelId={props.panelId}
      panelId={makeContactDetailsPanelId(props.panelId)}
      conversation={conversation}
      recipient={conversation.recipient}
      viewerState={viewerState}
      location={ContactDetailsPanelLocationsEnum.INBOXES}
      onTrackEvent={ContactDetailsTrackingHandler}
    />
  );
}

/*
 * Helpers.
 */

function isIntegrationFrameModel(frame: SidePanelFrame | undefined): frame is IntegrationModel {
  return Boolean(frame && frame.frameType === FrameTypesEnum.INTEGRATION && !frame.plugin);
}

function renderContactDetailsEmptyState() {
  return (
    <StyledContactDetailsEmptyStateWrapperDiv>
      <StyledContactDetailsEmptyStateMessageDiv>
        <Trans id="side-panel-contact-details-no-contacts">No contacts</Trans>
      </StyledContactDetailsEmptyStateMessageDiv>
    </StyledContactDetailsEmptyStateWrapperDiv>
  );
}

function renderPoppedOutNotifier() {
  return (
    <StyledPoppedOutWrapperDiv>
      <StyledPoppedOutIconWrapperDiv>
        <StyledIcon name="popout" size={64} />
      </StyledPoppedOutIconWrapperDiv>
      <Trans id="side-panel-popped-out">App open in an external window</Trans>
    </StyledPoppedOutWrapperDiv>
  );
}
